import { DashSourceProfilesDocument } from '@/graphql/generated/dash-source-profiles.generated';
import { DeleteCompetitorsDocument } from '@/graphql/generated/delete-competitor.generated';
import { PlanLimitsDocument } from '@/graphql/generated/plan-limits.generated';
import { TotalCompetitorsDocument } from '@/graphql/generated/total-competitors.generated';
import { DashSourceProfile, PlanLimit, TeamPermission } from '@/graphql/generated/types.generated';
import { useTeamPermissions } from '@/providers/team-permissions-provider';
import { useMutation } from '@apollo/client';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    useTheme,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

type DeleteCompetitorDialogProps = {
    competitor?: DashSourceProfile;
    competitors?: DashSourceProfile[];
    open?: boolean;
    onClose: () => void;
};

const DeleteCompetitorDialog = ({
    competitor,
    competitors = [],
    open = false,
    onClose,
}: DeleteCompetitorDialogProps) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { teamPermissions } = useTeamPermissions();
    const theme = useTheme();

    const [deleteCompetitor, { loading }] = useMutation(DeleteCompetitorsDocument, {
        onCompleted: () => {
            enqueueSnackbar(t('snackbar.competitor.deleted'));
            onClose();
        },
        onError: (e) => enqueueSnackbar(t(e.message)),
        refetchQueries: [
            DashSourceProfilesDocument,
            TotalCompetitorsDocument,
            {
                query: PlanLimitsDocument,
                variables: { planLimits: [PlanLimit.CompetitorsProfiles] },
            },
        ],
    });

    const hasCrudCompetitorsPerm = teamPermissions.includes(TeamPermission.CrudCompetitors);

    if (!hasCrudCompetitorsPerm) {
        return null;
    }

    return (
        <Dialog open={open} keepMounted slotProps={{ root: { style: { zIndex: theme.zIndex.modal + 1 } } }}>
            <DialogTitle>
                {competitor
                    ? t('competitor.modal.delete.singular.title', {
                          competitorName: competitor.name,
                      })
                    : t('competitor.modal.delete.multiple.title')}
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {competitor
                        ? t('competitor.modal.delete.singular.subtitle', {
                              competitorName: competitor.name,
                          })
                        : t('competitor.modal.delete.multiple.subtitle', {
                              count: competitors.length,
                          })}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('str.action.escape.cancel')}</Button>
                <Button
                    onClick={() =>
                        deleteCompetitor({
                            variables: {
                                competitors: [
                                    ...(competitor
                                        ? [
                                              {
                                                  dashSourceId: competitor.dashSource.id,
                                                  profileId: competitor.id,
                                              },
                                          ]
                                        : []),
                                    ...competitors.map((c) => ({
                                        dashSourceId: c.dashSource.id,
                                        profileId: c.id,
                                    })),
                                ],
                            },
                        })
                    }
                    loading={loading}
                >
                    {competitor
                        ? t('competitor-delete-dialog.delete-profile')
                        : t('competitor.modal.delete.action.delete')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
export default DeleteCompetitorDialog;
